import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Search, Loader2, Tag, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAT_LABELS = { vozila: 'Vozila', nekretnine: 'Nekretnine', posao: 'Posao', elektronika: 'Elektronika', namjestaj: 'Namještaj', usluge: 'Usluge', 'životinje': 'Životinje', ostalo: 'Ostalo' };
const CAT_COLORS = { vozila: '#1e90ff', nekretnine: '#22c55e', elektronika: '#8b5cf6', usluge: '#f97316', 'životinje': '#ec4899', namjestaj: '#eab308', ostalo: '#6b7280' };

export default function ClassifiedsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadItems(); }, [page, category]);

  async function loadItems() {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12 });
    if (category) params.set('category', category);
    if (q) params.set('q', q);
    try { const r = await fetch(`${API}/public/classifieds?${params}`); if (r.ok) { const d = await r.json(); setItems(d.classifieds || []); setTotal(d.total || 0); } }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }

  return (
    <>
      <Helmet><title>{`Oglasnik \u2014 e-mina.eu`}</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="classifieds-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">E-MINA Oglasnik</h1>
          <Link to="/oglasnik/predaj" className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg font-medium hover:bg-red-700" data-testid="submit-classified-link">Predaj oglas</Link>
        </div>
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg px-3">
            <Search size={16} className="text-gray-400" />
            <input type="text" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadItems()} placeholder="Pretraži oglase..." className="py-2.5 text-sm w-full outline-none" data-testid="classified-search" />
          </div>
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2.5 text-sm" data-testid="classified-cat-filter">
            <option value="">Sve kategorije</option>
            {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div> :
        items.length === 0 ? <div className="text-center py-12 text-gray-400">Nema oglasa</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map(item => {
              const img = item.images?.[0];
              const imgUrl = img ? (img.startsWith('http') || img.startsWith('data:') ? img : `${BACKEND}${img}`) : null;
              const promoted = item.promoted_until && new Date(item.promoted_until) > new Date();
              return (
                <Link key={item.id} to={`/oglasnik/${item.id}`}
                  className={`group bg-white dark:bg-[#111827] rounded-xl overflow-hidden hover:shadow-md transition-shadow ${promoted ? 'border-2 border-amber-400 shadow-amber-100 dark:shadow-none ring-1 ring-amber-300/40' : 'border border-gray-200 dark:border-gray-800'}`}
                  data-testid={`classified-card-${item.id}`}
                  data-promoted={promoted ? '1' : '0'}>
                  {imgUrl ? (
                    <div className="aspect-[4/3] bg-gray-100 dark:bg-[#0f172a] relative">
                      <img src={imgUrl} alt="" className="w-full h-full object-cover" data-testid={`classified-card-img-${item.id}`} />
                      {promoted && (
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-900 bg-amber-300 px-2 py-0.5 rounded-full shadow" data-testid="promoted-card-badge">
                          <Sparkles size={10} /> Istaknuto
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-gray-100 dark:bg-[#0f172a] flex items-center justify-center text-gray-300 dark:text-gray-600 relative">
                      <Tag size={28} />
                      {promoted && (
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-900 bg-amber-300 px-2 py-0.5 rounded-full shadow" data-testid="promoted-card-badge">
                          <Sparkles size={10} /> Istaknuto
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    <span className="inline-block text-[10px] font-bold uppercase text-white px-2 py-0.5 rounded mb-2" style={{ backgroundColor: CAT_COLORS[item.category] || '#6b7280' }}>
                      {CAT_LABELS[item.category] || item.category}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-red-600 transition-colors line-clamp-2 mb-2">{item.title}</h3>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1"><MapPin size={11} />{item.location}</span>
                      {item.price_eur != null ? <span className="font-bold text-gray-900 dark:text-gray-100">{item.price_eur.toLocaleString('hr')} €</span> : <span className="text-gray-400">Dogovor</span>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
